"use client";

import { Fragment, FormEvent, useEffect, useId, useRef, useState } from "react";
import { TimelineDatePicker } from "@/components/TimelineDatePicker";
import type { ContactModalConfig } from "@/lib/contact-modals";
import { submitToFormHandler } from "@/lib/form-handler";
import { RECAPTCHA_ACTIONS, loadRecaptcha } from "@/lib/recaptcha";
import styles from "./ContactRequestModal.module.css";

type FormState = "idle" | "submitting" | "success" | "error";

export function ContactRequestModal({
  config,
  isOpen,
  onClose,
}: {
  config: ContactModalConfig;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [formState, setFormState] = useState<FormState>("idle");
  const formRef = useRef<HTMLFormElement>(null);
  const titleId = useId();
  const fieldId = useId();
  const descriptionParagraphs = config.description.split("\n\n");

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Opening the modal is the earliest reliable signal that this visitor is going
  // to submit, and reCAPTCHA v3 needs the head start to score them by submit time.
  useEffect(() => {
    if (isOpen) void loadRecaptcha();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setFormState("idle");
      formRef.current?.reset();
    }
  }, [isOpen]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormState("submitting");
    const form = event.currentTarget;
    try {
      // config.id is "pentest" or "analysis", matching the infra/flashsyn endpoints.
      await submitToFormHandler(config.formAction, new FormData(form), RECAPTCHA_ACTIONS[config.id]);
      setFormState("success");
      form.reset();
    } catch {
      setFormState("error");
    }
  }

  if (!isOpen) return null;

  const closeButton = (
    <button type="button" className={`${styles.requestModalClose} ${styles.wInlineBlock}`} aria-label="Close" onClick={onClose}>
      <img src="/images/Vector.svg" alt="" className={styles.image95} />
    </button>
  );

  return (
    <div className={styles.analysisModalWrapper} style={{ display: "flex", opacity: 1 }}>
      <div className={styles.modalContainer} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={styles.requestModalHeader}>
          <div className={styles.requestModalHeaderTop}>
            <img src="/images/quanstamp-logo-on-light.svg" width={187} alt="Quantstamp logo" className={styles.requestAuditBrand} />
            <h2 id={titleId} className={styles.requestAuditTitle}>{config.title}</h2>
            <p className={styles.paragraph6}>
              {descriptionParagraphs.map((paragraph, index) => (
                <Fragment key={paragraph}>
                  {index > 0 && <><br /><br /></>}
                  {paragraph}
                </Fragment>
              ))}
              <br />
            </p>
            {config.logos?.length ? (
              <div>
                <div className={`${styles.infraLogos} ${styles.infraModal}`}>
                  {config.logos.map((logo) => (
                    <div className={styles.infraLogoDiv} key={logo.src}>
                      <img
                        src={logo.src}
                        srcSet={logo.srcSet}
                        sizes={logo.sizes}
                        loading="lazy"
                        alt={logo.alt}
                        className={[styles.infraClientLogo, logo.modifier && styles[logo.modifier], styles.infraModal]
                          .filter(Boolean)
                          .join(" ")}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* No `w-form` here: React owns this form's submit, and Webflow's
            delegated `.w-form form` handler throws on forms it never inited. */}
        <div className={styles.requestAuditPardotForm}>
          {formState === "success" ? (
            <div className={`${styles.successMessage2} ${styles.wFormDone}`} style={{ display: "block" }}>
              {closeButton}
              <div>
                <div className={styles.divBlock27}>
                  <img src="/images/solid-sharp-check-shield-1.svg" alt="" className={styles.image54} />
                  <div className={styles.textBlock24}>We received your request successfully</div>
                </div>
              </div>
              <p className={styles.paragraph4}>
                {config.successMessage}
                <br />
                <br />
              </p>
            </div>
          ) : (
            <>
              <form
                ref={formRef}
                action={config.formAction}
                method="post"
                className={`${styles.form3} ${styles.wClearfix}`}
                onSubmit={submit}
              >
                {closeButton}
                {config.fields.map((field, index) => (
                  <div className={index === 0 ? styles.divBlock35 : undefined} key={field.name}>
                    <label htmlFor={`${fieldId}-${field.name}`} className={styles.fieldLabel}>{field.label}</label>
                    {field.type === "date" ? (
                      <TimelineDatePicker
                        id={`${fieldId}-${field.name}`}
                        name={field.name}
                        inputClassName={`${styles.textField} ${styles.wInput}`}
                        required={field.required}
                      />
                    ) : field.type === "textarea" ? (
                      <textarea
                        id={`${fieldId}-${field.name}`}
                        name={field.name}
                        maxLength={5000}
                        required={field.required}
                        placeholder={field.placeholder}
                        className={`${styles.textField} ${styles.area} ${styles.wInput}`}
                      />
                    ) : (
                      <input
                        id={`${fieldId}-${field.name}`}
                        className={[styles.textField, field.type === "email" && styles.emailInputAuditRequest, styles.wInput]
                          .filter(Boolean)
                          .join(" ")}
                        maxLength={256}
                        name={field.name}
                        type={field.type}
                        required={field.required}
                        autoComplete={field.autoComplete}
                      />
                    )}
                  </div>
                ))}
                <div className={`${styles.v5LightCta} ${styles.mb40} ${styles.solutions}`}>
                  <input
                    type="submit"
                    className={`${styles.v5LightCtaLink3} ${styles.form} ${styles.wButton}`}
                    value={formState === "submitting" ? "Submitting..." : config.submitLabel}
                    disabled={formState === "submitting"}
                  />
                </div>
              </form>
              {formState === "error" && (
                <div className={`${styles.errorMessage2} ${styles.wFormFail}`} style={{ display: "block" }} role="alert">
                  <div className={styles.divBlock106}>
                    <img src="/images/error.svg" loading="lazy" alt="" />
                    <div className={styles.textBlock70}>Oops! Something went wrong while submitting the form.</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className={styles.modalCloseArea} role="presentation" onClick={onClose} />
    </div>
  );
}
